import React, { useState, useEffect, useCallback } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import {
  sessionReservations, isOpen, canAdd, defaultSelection, selectionCourse, selectionPlayers, rowsForPlayers,
  SessionPlayer, SessionReservation, SessionTeeTime, UnassignedGroup,
} from '../utils/sessionReservations';

// ── Types ──────────────────────────────────────────────────────────────────
interface CourseItem {
  course_id: string;
  course_name: string;
  facility_name?: string | null;
}

interface TeeboxItem {
  course_teebox_id: string;
  course_teebox_name: string;
}

interface PlayerListItem {
  player_id: number;
  full_name: string;
}

interface TournamentItem {
  tournament_id: string;
  tournament_name: string;
  start_date?: string;
  end_date?: string;
}

interface TeamItem {
  team_id: number;
  team_name: string;
  members: { player_id: number; full_name: string }[];
}

interface BonusEntry {
  player_id: number;
  points: string;
}

interface ResvPlayer {
  player_id: number;
  first_name: string;
  last_name: string;
}

interface ResvItem {
  reservation_id: number;
  reservation_name?: string;
  course_name: string;
  canon_course_name?: string;
  reservation_date?: string;
  tee_time?: string;
  num_players?: number;
  facility_id?: number;
  course_id?: number;
  facility_name?: string;
  players: ResvPlayer[];
}

interface PlayerEntry {
  player_id: number | null;
  display_name?: string;
  score: string;
  dq: boolean;
  dqReason: string;
  /** Penalty strokes assessed for this round — applied after the handicap. */
  pen: boolean;
  penalty: string;
  penaltyReason: string;
}

// Sanity bounds for a penalty: at least one stroke, and a cap that catches a
// typo (e.g. "22" for "2") without blocking any realistic assessment.
const PENALTY_MIN = 1;
const PENALTY_MAX = 20;

interface SchedReservation {
  reservation_id: number;
  reservation_date?: string;
  tee_time?: string;
  reservation_course_name?: string;
  num_players?: number;
}

// How far a session's scores are in: one round is expected per reservation.
interface EntrySummary {
  rounds_expected: number;
  rounds_entered: number;
  complete: boolean;
}

interface SchedRound {
  sched_round_id: number;
  round_number: number;
  course_id?: number;
  course_name?: string;
  slot_date?: string;
  am_pm?: string;
  is_leaderboard_round?: boolean;
  has_round?: boolean;            // every tee time of this session has been entered
  entry_summary?: EntrySummary | null;
  reservations?: SchedReservation[];
}

interface RoundGroupsResponse {
  round?: { course_id?: number | null; course_name?: string | null; slot_date?: string | null } | null;
  tee_times?: SessionTeeTime[];
  unassigned_groups?: UnassignedGroup[];
}

// ── Props ──────────────────────────────────────────────────────────────────
interface ModalProps {
  show: boolean;
  onHide: () => void;
  title?: string;
  children?: React.ReactNode;
  onRoundAdded?: () => void;
}

type Path = 'choose' | 'manual' | 'reservation' | 'tournament';

// ── Styles ─────────────────────────────────────────────────────────────────
const ms: Record<string, any> = {
  overlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(15,23,42,0.55)',
    zIndex: 2000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
  },
  box: {
    background: '#fff',
    borderRadius: 16,
    width: '100%',
    maxWidth: 700,
    maxHeight: 'calc(100vh - 32px)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxShadow: '0 24px 64px rgba(0,0,0,0.25)',
  },
  header: {
    padding: '16px 20px',
    borderBottom: '1px solid #f1f5f9',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    background: '#fff',
    flexShrink: 0,
  },
  headerTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: 700,
    color: '#0f172a',
    flex: 1,
  },
  closeBtn: {
    width: 30,
    height: 30,
    border: 'none',
    background: '#f1f5f9',
    borderRadius: 8,
    fontSize: 18,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#64748b',
    padding: 0,
  },
  backBtn: {
    border: 'none',
    background: 'transparent',
    color: '#2563eb',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  body: {
    flex: 1,
    overflowY: 'auto',
    padding: '24px',
  },
  // Path chooser
  chooseGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 14,
  },
  pathCard: {
    border: '1.5px solid #e2e8f0',
    borderRadius: 12,
    padding: '20px 14px',
    cursor: 'pointer',
    background: '#fff',
    textAlign: 'center',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 10,
  },
  pathIcon: {
    fontSize: 32,
  },
  pathTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: '#0f172a',
    margin: 0,
  },
  pathDesc: {
    fontSize: 12,
    color: '#64748b',
    margin: 0,
    lineHeight: 1.4,
  },
  // Form elements
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: '#64748b',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
    marginBottom: 12,
    borderBottom: '1px solid #f1f5f9',
    paddingBottom: 6,
  },
  row2: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
  },
  formGroup: {
    marginBottom: 14,
  },
  label: {
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    color: '#374151',
    marginBottom: 4,
  },
  input: {
    width: '100%',
    boxSizing: 'border-box' as const,
    border: '1px solid #d1d5db',
    borderRadius: 8,
    padding: '8px 10px',
    fontSize: 13,
    color: '#1e293b',
    background: '#fff',
    outline: 'none',
  },
  inputError: {
    borderColor: '#ef4444',
  },
  select: {
    width: '100%',
    boxSizing: 'border-box' as const,
    border: '1px solid #d1d5db',
    borderRadius: 8,
    padding: '8px 10px',
    fontSize: 13,
    color: '#1e293b',
    background: '#fff',
    outline: 'none',
    cursor: 'pointer',
  },
  errMsg: {
    color: '#ef4444',
    fontSize: 11,
    marginTop: 3,
  },
  holeToggle: {
    display: 'flex',
    border: '1px solid #d1d5db',
    borderRadius: 8,
    overflow: 'hidden',
    width: 'fit-content',
  },
  holeBtn: (active: boolean): React.CSSProperties => ({
    border: 'none',
    padding: '7px 18px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    background: active ? '#2563eb' : '#fff',
    color: active ? '#fff' : '#64748b',
  }),
  // Player row
  playerRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 90px 52px 52px 32px',
    gap: 8,
    alignItems: 'center',
    marginBottom: 8,
  },
  penBtn: (active: boolean, disabled: boolean): React.CSSProperties => ({
    border: 'none',
    borderRadius: 6,
    padding: '7px 0',
    fontSize: 11,
    fontWeight: 700,
    cursor: disabled ? 'not-allowed' : 'pointer',
    background: active ? '#fff7ed' : '#f1f5f9',
    color: active ? '#c2410c' : '#94a3b8',
    opacity: disabled ? 0.4 : 1,
  }),
  dqBtn: (active: boolean): React.CSSProperties => ({
    border: 'none',
    borderRadius: 6,
    padding: '7px 0',
    fontSize: 11,
    fontWeight: 700,
    cursor: 'pointer',
    background: active ? '#fef2f2' : '#f1f5f9',
    color: active ? '#dc2626' : '#94a3b8',
  }),
  removeBtn: {
    border: 'none',
    background: 'transparent',
    color: '#94a3b8',
    fontSize: 18,
    cursor: 'pointer',
    padding: 0,
    lineHeight: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  addPlayerBtn: {
    border: '1.5px dashed #cbd5e1',
    borderRadius: 8,
    padding: '8px 14px',
    fontSize: 13,
    fontWeight: 600,
    color: '#64748b',
    background: 'transparent',
    cursor: 'pointer',
    width: '100%',
    textAlign: 'center' as const,
    marginTop: 4,
  },
  readOnlyField: {
    padding: '8px 10px',
    background: '#f8fafc',
    borderRadius: 8,
    border: '1px solid #e2e8f0',
    fontSize: 13,
    color: '#374151',
    fontWeight: 500,
  },
  // Reservation list
  resvList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 8,
    maxHeight: 320,
    overflowY: 'auto' as const,
  },
  resvItem: (selected: boolean, disabled: boolean): React.CSSProperties => ({
    border: selected ? '1.5px solid #2563eb' : '1px solid #e2e8f0',
    borderRadius: 10,
    padding: '10px 12px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    background: selected ? '#eff6ff' : disabled ? '#f8fafc' : '#fff',
    opacity: disabled ? 0.5 : 1,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
  }),
  resvCheckbox: {
    marginTop: 2,
    accentColor: '#2563eb',
  },
  resvMeta: {
    fontSize: 12,
    color: '#64748b',
    marginTop: 2,
  },
  // Footer buttons
  footer: {
    padding: '14px 24px',
    borderTop: '1px solid #f1f5f9',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
    background: '#fff',
    flexShrink: 0,
  },
  cancelBtn: {
    border: '1px solid #d1d5db',
    borderRadius: 8,
    padding: '8px 18px',
    fontSize: 13,
    fontWeight: 600,
    background: '#fff',
    color: '#374151',
    cursor: 'pointer',
  },
  primaryBtn: (loading: boolean): React.CSSProperties => ({
    border: 'none',
    borderRadius: 8,
    padding: '8px 22px',
    fontSize: 13,
    fontWeight: 700,
    background: loading ? '#93c5fd' : '#2563eb',
    color: '#fff',
    cursor: loading ? 'not-allowed' : 'pointer',
  }),
  checkbox: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 13,
    color: '#374151',
    cursor: 'pointer',
  },
};

const APIURL = process.env.REACT_APP_API_URL;

const emptyPlayer = (): PlayerEntry => ({ player_id: null, score: '', dq: false, dqReason: '', pen: false, penalty: '', penaltyReason: '' });

// A score row for a player on a ticked reservation (or a blank row).
const reservationPlayerRow = (p: SessionPlayer | null): PlayerEntry =>
  p ? { ...emptyPlayer(), player_id: p.player_id, display_name: `${p.first_name} ${p.last_name}` } : emptyPlayer();

// ── Main Component ─────────────────────────────────────────────────────────
const Modal: React.FC<ModalProps> = ({ show, onHide, onRoundAdded }) => {
  const { getAccessTokenSilently } = useAuth0();

  // Path
  const [path, setPath] = useState<Path>('choose');
  const [reservStep, setReservStep] = useState<'pick' | 'score'>('pick');

  // Shared loaded data
  const [allPlayers, setAllPlayers] = useState<PlayerListItem[]>([]);
  const [courses, setCourses] = useState<CourseItem[]>([]);
  const [teeboxes, setTeeboxes] = useState<TeeboxItem[]>([]);

  // Shared form
  const [date, setDate] = useState<Date | null>(new Date());
  const [courseId, setCourseId] = useState('');
  const [teeboxId, setTeeboxId] = useState('');
  const [holes, setHoles] = useState<'9' | '18'>('18');
  const [playerEntries, setPlayerEntries] = useState<PlayerEntry[]>([emptyPlayer(), emptyPlayer()]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Manual path
  const [leftyRighty, setLeftyRighty] = useState(false);
  const [manualTournamentId, setManualTournamentId] = useState('');
  const [openTournaments, setOpenTournaments] = useState<TournamentItem[]>([]);

  // Team competition bonus (regular rounds only)
  const [teams, setTeams] = useState<TeamItem[]>([]);
  const [bonusEntries, setBonusEntries] = useState<BonusEntry[]>([]);
  const [bonusTeamId, setBonusTeamId] = useState('');
  const [bonusTeamPoints, setBonusTeamPoints] = useState('');
  const [bonusPlayerId, setBonusPlayerId] = useState('');
  const [bonusPlayerPoints, setBonusPlayerPoints] = useState('');

  // Reservation path
  const [reservations, setReservations] = useState<ResvItem[]>([]);
  const [selectedResvIds, setSelectedResvIds] = useState<Set<number>>(new Set());
  const [resvFacilityId, setResvFacilityId] = useState<number | null>(null);
  const [resvCourseId, setResvCourseId] = useState<number | null>(null);
  const [resvCourseName, setResvCourseName] = useState('');
  const [resvReadOnlyCourse, setResvReadOnlyCourse] = useState('');

  // Tournament path
  const [tournamentForRound, setTournamentForRound] = useState('');
  const [openTournamentsForRound, setOpenTournamentsForRound] = useState<TournamentItem[]>([]);
  const [schedRounds, setSchedRounds] = useState<SchedRound[]>([]);
  const [allSchedGroups, setAllSchedGroups] = useState<{ round_number: number; slot_date?: string }[]>([]);
  const [selectedDay, setSelectedDay] = useState('');       // slot_date
  const [selectedSession, setSelectedSession] = useState(''); // 'AM' | 'PM'
  const [selectedRoundNum, setSelectedRoundNum] = useState<number | null>(null);
  // The session's reservations, and the ones ticked for this round.
  const [resvList, setResvList] = useState<SessionReservation[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [sessionCourse, setSessionCourse] = useState<{ id: number | null; name: string }>({ id: null, name: '' });
  const [tournReadOnlyCourse, setTournReadOnlyCourse] = useState('');
  const [tournReadOnlyDate, setTournReadOnlyDate] = useState('');

  const selectedResvs = resvList.filter(r => selectedKeys.includes(r.key));
  // The round links the ticked reservations — each can only ever have one round.
  const tournResvIds = selectedResvs.map(r => r.reservation_id).filter((id): id is number => id != null);

  // Reset everything when modal closes
  useEffect(() => {
    if (!show) {
      setPath('choose');
      setReservStep('pick');
      setDate(new Date());
      setCourseId('');
      setTeeboxId('');
      setTeeboxes([]);
      setHoles('18');
      setLeftyRighty(false);
      setBonusEntries([]);
      setBonusTeamId(''); setBonusTeamPoints(''); setBonusPlayerId(''); setBonusPlayerPoints('');
      setPlayerEntries([emptyPlayer(), emptyPlayer()]);
      setManualTournamentId('');
      setSelectedResvIds(new Set());
      setResvFacilityId(null);
      setResvCourseId(null);
      setResvCourseName('');
      setResvReadOnlyCourse('');
      setTournamentForRound('');
      setSchedRounds([]);
      setSelectedDay('');
      setSelectedSession('');
      setSelectedRoundNum(null);
      setResvList([]);
      setSelectedKeys([]);
      setSessionCourse({ id: null, name: '' });
      setTournReadOnlyCourse('');
      setTournReadOnlyDate('');
      setErrors({});
    }
  }, [show]);

  // Load players + courses + teams once
  useEffect(() => {
    const load = async () => {
      const token = await getAccessTokenSilently();
      const [pr, cr, tr] = await Promise.all([
        fetch(`${APIURL}/playersAddedtoRound`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${APIURL}/coursesv2`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${APIURL}/team-competition`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setAllPlayers(await pr.json());
      setCourses(await cr.json());
      try { setTeams((await tr.json()).teams || []); } catch { /* teams optional */ }
    };
    load().catch(console.error);
  }, [getAccessTokenSilently]);

  // Fetch teeboxes when courseId changes. A reply for a course that has since
  // changed again (ticking reservations back and forth) is dropped, so the list
  // always matches the course shown.
  useEffect(() => {
    if (!courseId) { setTeeboxes([]); setTeeboxId(''); return; }
    let stale = false;
    getAccessTokenSilently().then(async token => {
      const r = await fetch(`${APIURL}/teeboxes?id=${courseId}`, { headers: { Authorization: `Bearer ${token}` } });
      const list = await r.json();
      if (stale) return;
      setTeeboxes(list);
      setTeeboxId('');
    }).catch(console.error);
    return () => { stale = true; };
  }, [courseId, getAccessTokenSilently]);

  // Load unlinked reservations when path = reservation
  useEffect(() => {
    if (path !== 'reservation') return;
    getAccessTokenSilently().then(async token => {
      const r = await fetch(`${APIURL}/reservations/for-round`, { headers: { Authorization: `Bearer ${token}` } });
      setReservations(await r.json());
    }).catch(console.error);
  }, [path, getAccessTokenSilently]);

  // Load tournaments for manual path
  useEffect(() => {
    if (path !== 'manual') return;
    getAccessTokenSilently().then(async token => {
      const r = await fetch(`${APIURL}/tournaments?tournament_status=open`, { headers: { Authorization: `Bearer ${token}` } });
      setOpenTournaments(await r.json());
    }).catch(console.error);
  }, [path, getAccessTokenSilently]);

  // Load OPEN tournaments for tournament path
  useEffect(() => {
    if (path !== 'tournament') return;
    getAccessTokenSilently().then(async token => {
      const r = await fetch(`${APIURL}/tournaments?tournament_status=open`, { headers: { Authorization: `Bearer ${token}` } });
      setOpenTournamentsForRound(await r.json());
    }).catch(console.error);
  }, [path, getAccessTokenSilently]);

  // Scheduler rounds (sessions) of a tournament, with how far each is entered.
  const loadSchedule = useCallback(async (tournamentId: string) => {
    const token = await getAccessTokenSilently();
    const r = await fetch(`${APIURL}/tournaments/${tournamentId}/scheduler`, { headers: { Authorization: `Bearer ${token}` } });
    const data = await r.json();
    setSchedRounds(data.rounds || []);
    setAllSchedGroups(data.schedule?.groups || []);
  }, [getAccessTokenSilently]);

  // Load scheduler rounds when tournamentForRound changes
  useEffect(() => {
    setSelectedDay('');
    setSelectedSession('');
    setSelectedRoundNum(null);
    if (!tournamentForRound) {
      setSchedRounds([]); setAllSchedGroups([]);
      return;
    }
    loadSchedule(tournamentForRound).catch(console.error);
  }, [tournamentForRound, loadSchedule]);

  // Derive the matching scheduler round from (day, session)
  useEffect(() => {
    if (!selectedDay || !selectedSession) { setSelectedRoundNum(null); return; }
    const match = schedRounds.find(r => r.slot_date === selectedDay && r.am_pm === selectedSession);
    setSelectedRoundNum(match ? match.round_number : null);
  }, [selectedDay, selectedSession, schedRounds]);

  // Load the session's reservations when a round is resolved.
  useEffect(() => {
    if (!tournamentForRound || selectedRoundNum == null) {
      setResvList([]); setSelectedKeys([]); setSessionCourse({ id: null, name: '' });
      return;
    }
    const round = schedRounds.find(r => r.round_number === selectedRoundNum);
    let stale = false;
    getAccessTokenSilently().then(async token => {
      const r = await fetch(`${APIURL}/tournaments/${tournamentForRound}/round-groups/${selectedRoundNum}`, { headers: { Authorization: `Bearer ${token}` } });
      const data: RoundGroupsResponse = await r.json();
      if (stale) return;
      const list = sessionReservations(data.tee_times, data.unassigned_groups);
      setResvList(list);
      setSelectedKeys(defaultSelection(list));
      setSessionCourse({
        id: data.round?.course_id ?? round?.course_id ?? null,
        name: data.round?.course_name || round?.course_name || '',
      });

      // Auto-fill the date from the round
      const slotDate = data.round?.slot_date || round?.slot_date;
      if (slotDate) {
        setTournReadOnlyDate(slotDate);
        setDate(new Date(slotDate + 'T12:00:00'));
      }
    }).catch(console.error);
    return () => { stale = true; };
  }, [tournamentForRound, selectedRoundNum, schedRounds, getAccessTokenSilently]);

  // The ticked reservations decide the round: their players, and their course —
  // where they actually play (the booking's course), else the round's own.
  useEffect(() => {
    if (path !== 'tournament') return;
    const selected = resvList.filter(r => selectedKeys.includes(r.key));
    setPlayerEntries(prev => rowsForPlayers(selectionPlayers(selected), prev, reservationPlayerRow));
    const course = selectionCourse(selected);
    const id = course.course_id ?? sessionCourse.id;
    if (id != null) {
      setCourseId(String(id));
      setTournReadOnlyCourse(course.course_id != null ? (course.course_name || '') : sessionCourse.name);
    } else {
      // Nothing names the course: the organiser picks it, and the pick stays.
      setTournReadOnlyCourse('');
    }
  }, [path, resvList, selectedKeys, sessionCourse]);

  // Tick or untick a reservation. Only reservations on the same course can be
  // entered together — a round has one course.
  const toggleReservation = (r: SessionReservation) => {
    if (!isOpen(r)) return;
    setErrors(prev => { const e = { ...prev }; delete e.parts; return e; });
    setSelectedKeys(prev => {
      if (prev.includes(r.key)) return prev.filter(k => k !== r.key);
      return canAdd(r, resvList.filter(x => prev.includes(x.key))) ? [...prev, r.key] : prev;
    });
  };

  // ── Reservation selection ────────────────────────────────────────────────
  const canSelectResv = (resv: ResvItem): boolean => {
    if (selectedResvIds.size === 0) return true;
    if (resvFacilityId != null) return resv.facility_id === resvFacilityId;
    if (resvCourseId != null) return resv.course_id === resvCourseId;
    return resv.course_name === resvCourseName;
  };

  const toggleResv = (resv: ResvItem) => {
    if (!canSelectResv(resv) && !selectedResvIds.has(resv.reservation_id)) {
      setErrors({ reservations: 'All reservations must be from the same facility and course.' });
      return;
    }
    const next = new Set(selectedResvIds);
    if (next.has(resv.reservation_id)) {
      next.delete(resv.reservation_id);
      if (next.size === 0) {
        setResvFacilityId(null);
        setResvCourseId(null);
        setResvCourseName('');
        setResvReadOnlyCourse('');
        setCourseId('');
        setDate(new Date());
      }
    } else {
      next.add(resv.reservation_id);
      if (next.size === 1) {
        setResvFacilityId(resv.facility_id ?? null);
        setResvCourseId(resv.course_id ?? null);
        setResvCourseName(resv.canon_course_name || resv.course_name);
        setResvReadOnlyCourse(resv.canon_course_name || resv.facility_name || resv.course_name);
        if (resv.course_id) setCourseId(String(resv.course_id));
        if (resv.reservation_date) setDate(new Date(resv.reservation_date + 'T12:00:00'));
      }
    }
    setSelectedResvIds(next);
    setErrors(prev => { const e = { ...prev }; delete e.reservations; return e; });
  };

  const proceedToScore = () => {
    if (selectedResvIds.size === 0) {
      setErrors({ reservations: 'Select at least one reservation.' });
      return;
    }
    const selResvs = reservations.filter(r => selectedResvIds.has(r.reservation_id));
    const seen = new Set<number>();
    const entries: PlayerEntry[] = [];
    for (const resv of selResvs) {
      for (const p of resv.players) {
        if (!seen.has(p.player_id)) {
          seen.add(p.player_id);
          entries.push({ player_id: p.player_id, display_name: `${p.first_name} ${p.last_name}`, score: '', dq: false, dqReason: '', pen: false, penalty: '', penaltyReason: '' });
        }
      }
    }
    while (entries.length < 2) entries.push(emptyPlayer());
    setPlayerEntries(entries);
    setReservStep('score');
  };

  // ── Player helpers ───────────────────────────────────────────────────────
  const updateEntry = (i: number, field: keyof PlayerEntry, value: any) => {
    setPlayerEntries(prev => {
      const next = [...prev];
      next[i] = { ...next[i], [field]: value };
      return next;
    });
    setErrors(prev => {
      const e = { ...prev };
      delete e[`player_${i}`];
      delete e[`score_${i}`];
      delete e[`penalty_${i}`];
      delete e[`penaltyReason_${i}`];
      return e;
    });
  };

  // A DQ supersedes any penalty (the player is last regardless), so flipping
  // DQ on clears the penalty entry for that row.
  const toggleDq = (i: number) => {
    setPlayerEntries(prev => {
      const next = [...prev];
      const dq = !next[i].dq;
      next[i] = dq
        ? { ...next[i], dq, pen: false, penalty: '', penaltyReason: '' }
        : { ...next[i], dq };
      return next;
    });
    setErrors(prev => {
      const e = { ...prev };
      delete e[`score_${i}`];
      delete e[`dqReason_${i}`];
      delete e[`penalty_${i}`];
      delete e[`penaltyReason_${i}`];
      return e;
    });
  };

  const togglePen = (i: number) => {
    setPlayerEntries(prev => {
      const next = [...prev];
      const pen = !next[i].pen;
      next[i] = pen ? { ...next[i], pen } : { ...next[i], pen, penalty: '', penaltyReason: '' };
      return next;
    });
    setErrors(prev => {
      const e = { ...prev };
      delete e[`penalty_${i}`];
      delete e[`penaltyReason_${i}`];
      return e;
    });
  };

  const addPlayerRow = () => setPlayerEntries(prev => [...prev, emptyPlayer()]);

  const removePlayerRow = (i: number) => {
    if (playerEntries.length <= 2) return;
    setPlayerEntries(prev => prev.filter((_, idx) => idx !== i));
  };

  const usedPlayerIds = playerEntries.map(p => p.player_id).filter(Boolean) as number[];
  const availableFor = (i: number) =>
    allPlayers.filter(p => !usedPlayerIds.includes(p.player_id) || playerEntries[i].player_id === p.player_id);

  // ── Validation ───────────────────────────────────────────────────────────
  const validate = (isTournament: boolean): boolean => {
    const errs: Record<string, string> = {};
    if (!date) errs.date = 'Date required.';
    if (!isTournament && path === 'manual' && !courseId) errs.course = 'Course required.';
    if (isTournament && !tournamentForRound) errs.tournament = 'Select a tournament.';
    if (isTournament && selectedRoundNum == null) errs.round = 'Select a round.';
    if (isTournament && resvList.length > 0 && selectedResvs.length === 0) {
      errs.parts = 'Pick the reservation(s) this round was played on.';
    }
    if (!teeboxId) errs.teebox = 'Tee box required.';
    playerEntries.forEach((p, i) => {
      if (p.player_id == null) errs[`player_${i}`] = 'Select a player.';
      if (!p.dq) {
        const sc = Number(p.score);
        if (p.score === '' || isNaN(sc) || sc < 50 || sc > 144) {
          errs[`score_${i}`] = 'Score must be 50–144.';
        }
      } else if (!p.dqReason.trim()) {
        // A DQ must always be explained.
        errs[`dqReason_${i}`] = 'A DQ reason is required.';
      }
      if (p.pen && !p.dq) {
        const pen = Number(p.penalty);
        if (p.penalty === '' || !Number.isInteger(pen) || pen < PENALTY_MIN || pen > PENALTY_MAX) {
          errs[`penalty_${i}`] = `Penalty must be ${PENALTY_MIN}–${PENALTY_MAX} strokes.`;
        }
        // A penalty must always be explained — it's what the PEN tooltip shows.
        if (!p.penaltyReason.trim()) {
          errs[`penaltyReason_${i}`] = 'A penalty reason is required.';
        }
      }
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── Save ─────────────────────────────────────────────────────────────────
  const save = async (isTournament: boolean) => {
    if (!validate(isTournament)) return;
    setSaving(true);
    const localDate = new Date(date!.getTime() - date!.getTimezoneOffset() * 60000).toISOString().split('T')[0];
    const roundData: Record<string, any> = {
      date: localDate,
      course: courseId,
      courseteebox: teeboxId,
      tournamentid: isTournament ? tournamentForRound : (manualTournamentId || undefined),
      players: playerEntries.map(p => ({
        player_id: p.player_id,
        score: Number(p.score) || 0,
        is_disqualified: p.dq,
        dq_reason: p.dq ? p.dqReason.trim() : null,
        // Penalty strokes are added to the net score (after handicap) server-side.
        penalty_strokes: p.pen && !p.dq ? Number(p.penalty) : 0,
        penalty_reason: p.pen && !p.dq ? p.penaltyReason.trim() : null,
      })),
      lefty_vs_righty: !isTournament && path === 'manual' ? leftyRighty : false,
      number_of_holes: holes,
    };
    if (!isTournament && bonusEntries.length > 0) {
      roundData.bonus_overrides = bonusEntries
        .map(e => ({ player_id: e.player_id, points: Math.round(Number(e.points)) }))
        .filter(e => Number.isInteger(e.player_id) && e.points > 0);
    }
    if (path === 'reservation' && selectedResvIds.size > 0) {
      roundData.reservation_ids = Array.from(selectedResvIds);
    } else if (path === 'tournament' && tournResvIds.length > 0) {
      roundData.reservation_ids = tournResvIds;
    }
    try {
      const token = await getAccessTokenSilently();
      await axios.post(`${APIURL}/addNewRound`, { roundData }, { headers: { Authorization: `Bearer ${token}` } });

      alert('Round saved successfully!');
      onHide();
      onRoundAdded?.();
    } catch (err: any) {
      console.error('Save failed', err);
      const serverMsg = err?.response?.data?.error;
      alert(serverMsg || 'Failed to save round. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── Shared: course + teebox selectors ───────────────────────────────────
  const renderCourseSelect = () => {
    const groups = courses.reduce((acc: Record<string, CourseItem[]>, c) => {
      const key = c.facility_name || 'Other';
      (acc[key] = acc[key] || []).push(c);
      return acc;
    }, {});
    return (
      <div style={ms.formGroup}>
        <label style={ms.label}>Course</label>
        <select
          style={{ ...ms.select, ...(errors.course ? ms.inputError : {}) }}
          value={courseId}
          onChange={e => { setCourseId(e.target.value); setErrors(prev => { const e2 = { ...prev }; delete e2.course; return e2; }); }}
        >
          <option value="" disabled>Select a course</option>
          {Object.entries(groups).map(([fac, items]) => (
            <optgroup key={fac} label={fac}>
              {items.map(item => (
                <option key={item.course_id} value={item.course_id}>{item.course_name}</option>
              ))}
            </optgroup>
          ))}
        </select>
        {errors.course && <div style={ms.errMsg}>{errors.course}</div>}
      </div>
    );
  };

  const renderTeeboxSelect = () => (
    <div style={ms.formGroup}>
      <label style={ms.label}>Tee Box</label>
      {teeboxes.length === 0 && !courseId ? (
        <div style={ms.readOnlyField}>Select a course first</div>
      ) : teeboxes.length === 0 ? (
        <div style={ms.readOnlyField}>Loading teeboxes…</div>
      ) : (
        <select
          style={{ ...ms.select, ...(errors.teebox ? ms.inputError : {}) }}
          value={teeboxId}
          onChange={e => { setTeeboxId(e.target.value); setErrors(prev => { const e2 = { ...prev }; delete e2.teebox; return e2; }); }}
        >
          <option value="" disabled>Select a tee box</option>
          {teeboxes.map(t => (
            <option key={t.course_teebox_id} value={t.course_teebox_id}>{t.course_teebox_name}</option>
          ))}
        </select>
      )}
      {errors.teebox && <div style={ms.errMsg}>{errors.teebox}</div>}
    </div>
  );

  const renderHolesToggle = () => (
    <div style={ms.formGroup}>
      <label style={ms.label}>Holes</label>
      <div style={ms.holeToggle}>
        {(['18', '9'] as const).map(h => (
          <button key={h} type="button" style={ms.holeBtn(holes === h)} onClick={() => setHoles(h)}>{h} holes</button>
        ))}
      </div>
    </div>
  );

  const renderDatePicker = (readOnly = false, readOnlyVal = '') => (
    <div style={ms.formGroup}>
      <label style={ms.label}>Date</label>
      {readOnly && readOnlyVal ? (
        <div style={ms.readOnlyField}>{readOnlyVal}</div>
      ) : (
        <>
          <DatePicker
            selected={date}
            onChange={d => { setDate(d); setErrors(prev => { const e2 = { ...prev }; delete e2.date; return e2; }); }}
            dateFormat="yyyy-MM-dd"
            className={errors.date ? 'form-control is-invalid' : 'form-control'}
            wrapperClassName="datepicker-full"
          />
          {errors.date && <div style={ms.errMsg}>{errors.date}</div>}
        </>
      )}
    </div>
  );

  // ── Shared: player score entry ───────────────────────────────────────────
  const renderPlayerEntry = (isTournament: boolean) => (
    <div style={ms.section}>
      <div style={ms.sectionTitle}>Players & Scores</div>
      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 10 }}>
        Player · Gross Score · PEN · DQ
      </div>
      {playerEntries.map((p, i) => {
        const locked = (path === 'reservation' || isTournament) && p.display_name != null;
        return (
          <div key={i} style={ms.playerRow}>
            {locked ? (
              <div style={{ ...ms.readOnlyField, fontSize: 13 }}>{p.display_name}</div>
            ) : (
              <div>
                <select
                  style={{ ...ms.select, ...(errors[`player_${i}`] ? ms.inputError : {}) }}
                  value={p.player_id !== null ? String(p.player_id) : ''}
                  onChange={e => updateEntry(i, 'player_id', Number(e.target.value))}
                >
                  <option value="" disabled>Select player</option>
                  {availableFor(i).map(pl => (
                    <option key={pl.player_id} value={pl.player_id}>{pl.full_name}</option>
                  ))}
                </select>
                {errors[`player_${i}`] && <div style={ms.errMsg}>{errors[`player_${i}`]}</div>}
              </div>
            )}
            <div>
              <input
                type="number"
                min={50}
                max={144}
                placeholder="Score"
                disabled={p.dq}
                value={p.dq ? '' : p.score}
                onChange={e => updateEntry(i, 'score', e.target.value)}
                onKeyDown={e => ['e', '.', '+', '-'].includes(e.key) && e.preventDefault()}
                style={{ ...ms.input, ...(errors[`score_${i}`] ? ms.inputError : {}), opacity: p.dq ? 0.4 : 1 }}
              />
              {errors[`score_${i}`] && <div style={ms.errMsg}>{errors[`score_${i}`]}</div>}
            </div>
            <button
              type="button"
              style={ms.penBtn(p.pen, p.dq)}
              disabled={p.dq}
              onClick={() => togglePen(i)}
              title={p.dq ? 'A disqualified player takes no penalty' : p.pen ? 'Remove penalty' : 'Assess penalty strokes'}
            >
              PEN
            </button>
            <button
              type="button"
              style={ms.dqBtn(p.dq)}
              onClick={() => toggleDq(i)}
              title={p.dq ? 'Mark as not DQ' : 'Mark as DQ'}
            >
              DQ
            </button>
            <button
              type="button"
              style={{ ...ms.removeBtn, visibility: playerEntries.length > 2 ? 'visible' : 'hidden' }}
              onClick={() => removePlayerRow(i)}
              title="Remove player"
            >
              ×
            </button>
            {p.pen && !p.dq && (
              <div style={{ gridColumn: '1 / -1', marginTop: 2, display: 'grid', gridTemplateColumns: '90px 1fr', gap: 8, alignItems: 'start' }}>
                <div>
                  <input
                    type="number"
                    min={PENALTY_MIN}
                    max={PENALTY_MAX}
                    placeholder="Strokes"
                    value={p.penalty}
                    onChange={e => updateEntry(i, 'penalty', e.target.value)}
                    onKeyDown={e => ['e', '.', '+', '-'].includes(e.key) && e.preventDefault()}
                    style={{ ...ms.input, ...(errors[`penalty_${i}`] ? ms.inputError : {}) }}
                    aria-label="Penalty strokes"
                  />
                  {errors[`penalty_${i}`] && <div style={ms.errMsg}>{errors[`penalty_${i}`]}</div>}
                </div>
                <div>
                  <input
                    type="text"
                    placeholder="Reason for penalty (required) — e.g. wrong ball, drop not taken…"
                    value={p.penaltyReason}
                    onChange={e => updateEntry(i, 'penaltyReason', e.target.value)}
                    style={{ ...ms.input, width: '100%', ...(errors[`penaltyReason_${i}`] ? ms.inputError : {}) }}
                    aria-label="Penalty reason"
                  />
                  {errors[`penaltyReason_${i}`] && <div style={ms.errMsg}>{errors[`penaltyReason_${i}`]}</div>}
                </div>
                {p.score !== '' && p.penalty !== '' && !errors[`penalty_${i}`] && Number(p.penalty) >= PENALTY_MIN && (
                  <div style={{ gridColumn: '1 / -1', fontSize: 11, color: '#c2410c' }}>
                    +{Number(p.penalty)} stroke{Number(p.penalty) === 1 ? '' : 's'} will be added to the net score after the handicap.
                  </div>
                )}
              </div>
            )}
            {p.dq && (
              <div style={{ gridColumn: '1 / -1', marginTop: 2 }}>
                <input
                  type="text"
                  placeholder="Reason for DQ (required) — e.g. signed for wrong score, no-show…"
                  value={p.dqReason}
                  onChange={e => updateEntry(i, 'dqReason', e.target.value)}
                  style={{ ...ms.input, width: '100%', ...(errors[`dqReason_${i}`] ? ms.inputError : {}) }}
                />
                {errors[`dqReason_${i}`] && <div style={ms.errMsg}>{errors[`dqReason_${i}`]}</div>}
              </div>
            )}
          </div>
        );
      })}
      <button type="button" style={ms.addPlayerBtn} onClick={addPlayerRow}>+ Add Player</button>
    </div>
  );

  // ── Team competition bonus helpers ───────────────────────────────────────
  const playerName = (pid: number) => allPlayers.find(p => p.player_id === pid)?.full_name || `Player ${pid}`;

  const upsertBonus = (player_id: number, points: string) =>
    setBonusEntries(prev => {
      const next = prev.filter(e => e.player_id !== player_id);
      next.push({ player_id, points });
      return next;
    });

  const addTeamBonus = () => {
    const team = teams.find(t => String(t.team_id) === bonusTeamId);
    const pts = Number(bonusTeamPoints);
    if (!team || !bonusTeamPoints || isNaN(pts) || pts <= 0) return;
    setBonusEntries(prev => {
      const map = new Map(prev.map(e => [e.player_id, e]));
      team.members.forEach(m => map.set(m.player_id, { player_id: m.player_id, points: String(pts) }));
      return Array.from(map.values());
    });
    setBonusTeamId(''); setBonusTeamPoints('');
  };

  const addPlayerBonus = () => {
    const pid = Number(bonusPlayerId);
    const pts = Number(bonusPlayerPoints);
    if (!bonusPlayerId || !bonusPlayerPoints || isNaN(pts) || pts <= 0) return;
    upsertBonus(pid, String(pts));
    setBonusPlayerId(''); setBonusPlayerPoints('');
  };

  const renderBonusSection = () => (
    <div style={ms.section}>
      <div style={ms.sectionTitle}>Team Competition Bonus (optional)</div>
      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 10 }}>
        Award bonus team points for this round — to a whole team, or to individual players.
        These add to a player's team points in the standings.
      </div>

      {teams.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px auto', gap: 8, alignItems: 'center', marginBottom: 8 }}>
          <select style={ms.select} value={bonusTeamId} onChange={e => setBonusTeamId(e.target.value)}>
            <option value="">Add a whole team…</option>
            {teams.map(t => <option key={t.team_id} value={t.team_id}>{t.team_name} ({t.members.length})</option>)}
          </select>
          <input type="number" min={1} placeholder="Pts" style={ms.input} value={bonusTeamPoints}
            onChange={e => setBonusTeamPoints(e.target.value)} />
          <button type="button" style={ms.cancelBtn} onClick={addTeamBonus} disabled={!bonusTeamId || !bonusTeamPoints}>Add</button>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px auto', gap: 8, alignItems: 'center', marginBottom: 12 }}>
        <select style={ms.select} value={bonusPlayerId} onChange={e => setBonusPlayerId(e.target.value)}>
          <option value="">Add a player…</option>
          {allPlayers.map(p => <option key={p.player_id} value={p.player_id}>{p.full_name}</option>)}
        </select>
        <input type="number" min={1} placeholder="Pts" style={ms.input} value={bonusPlayerPoints}
          onChange={e => setBonusPlayerPoints(e.target.value)} />
        <button type="button" style={ms.cancelBtn} onClick={addPlayerBonus} disabled={!bonusPlayerId || !bonusPlayerPoints}>Add</button>
      </div>

      {bonusEntries.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {bonusEntries.map(e => (
            <div key={e.player_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '6px 10px', fontSize: 13 }}>
              <span style={{ color: '#374151' }}>{playerName(e.player_id)}</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <strong style={{ color: '#2563eb' }}>+{e.points}</strong>
                <button type="button" style={ms.removeBtn} title="Remove"
                  onClick={() => setBonusEntries(prev => prev.filter(x => x.player_id !== e.player_id))}>×</button>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ── Path renderers ───────────────────────────────────────────────────────
  const renderChoose = () => (
    <div>
      <p style={{ fontSize: 14, color: '#64748b', marginTop: 0, marginBottom: 20 }}>
        How would you like to add this round?
      </p>
      <div style={ms.chooseGrid}>
        {[
          { key: 'manual' as Path, icon: '⛳', title: 'Manual Entry', desc: 'Enter all round details manually' },
          { key: 'reservation' as Path, icon: '📋', title: 'From Reservations', desc: 'Import players from existing tee time reservations' },
          { key: 'tournament' as Path, icon: '🏆', title: 'Tournament Match', desc: 'Add a tournament round from its reservation — players and course come from the schedule' },
        ].map(({ key, icon, title, desc }) => (
          <div
            key={key}
            style={ms.pathCard}
            onClick={() => setPath(key)}
            onMouseEnter={e => {
              (e.currentTarget as HTMLDivElement).style.borderColor = '#2563eb';
              (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 12px rgba(37,99,235,0.12)';
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLDivElement).style.borderColor = '#e2e8f0';
              (e.currentTarget as HTMLDivElement).style.boxShadow = 'none';
            }}
          >
            <span style={ms.pathIcon}>{icon}</span>
            <p style={ms.pathTitle}>{title}</p>
            <p style={ms.pathDesc}>{desc}</p>
          </div>
        ))}
      </div>
    </div>
  );

  const renderManual = () => (
    <div>
      <div style={ms.section}>
        <div style={ms.sectionTitle}>Round Details</div>
        <div style={ms.row2}>
          {renderDatePicker()}
          {renderHolesToggle()}
        </div>
        {renderCourseSelect()}
        {renderTeeboxSelect()}
        {openTournaments.length > 0 && (
          <div style={ms.formGroup}>
            <label style={ms.label}>Tournament (optional)</label>
            <select
              style={ms.select}
              value={manualTournamentId}
              onChange={e => setManualTournamentId(e.target.value)}
            >
              <option value="">No tournament</option>
              {openTournaments.map(t => (
                <option key={t.tournament_id} value={t.tournament_id}>{t.tournament_name}</option>
              ))}
            </select>
          </div>
        )}
        <label style={{ ...ms.checkbox, marginTop: 4 }}>
          <input
            type="checkbox"
            checked={leftyRighty}
            onChange={e => setLeftyRighty(e.target.checked)}
            style={{ accentColor: '#2563eb' }}
          />
          Team competition round
        </label>
      </div>
      {renderPlayerEntry(false)}
      {renderBonusSection()}
    </div>
  );

  const renderReservation = () => {
    if (reservStep === 'pick') {
      return (
        <div>
          <div style={ms.section}>
            <div style={ms.sectionTitle}>Select Reservations</div>
            <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 12px' }}>
              Choose one or more reservations. They must all be at the same facility and course.
            </p>
            {errors.reservations && <div style={{ ...ms.errMsg, marginBottom: 8 }}>{errors.reservations}</div>}
            {reservations.length === 0 ? (
              <div style={{ padding: '20px 0', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                No unlinked reservations found.
              </div>
            ) : (
              <div style={ms.resvList}>
                {reservations.map(resv => {
                  const selected = selectedResvIds.has(resv.reservation_id);
                  const disabled = !selected && !canSelectResv(resv);
                  return (
                    <div
                      key={resv.reservation_id}
                      style={ms.resvItem(selected, disabled)}
                      onClick={() => !disabled && toggleResv(resv)}
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        style={ms.resvCheckbox}
                        readOnly
                        disabled={disabled}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, color: '#0f172a' }}>
                          {resv.facility_name || resv.canon_course_name || resv.course_name}
                          {resv.reservation_date && <span style={{ color: '#64748b', fontWeight: 400, marginLeft: 8 }}>{resv.reservation_date}</span>}
                          {resv.tee_time && <span style={{ color: '#94a3b8', marginLeft: 6, fontSize: 12 }}>{resv.tee_time}</span>}
                        </div>
                        {resv.canon_course_name && resv.canon_course_name !== resv.facility_name && (
                          <div style={ms.resvMeta}>{resv.canon_course_name}</div>
                        )}
                        {resv.players.length > 0 && (
                          <div style={ms.resvMeta}>
                            {resv.players.map(p => `${p.first_name} ${p.last_name}`).join(', ')}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      );
    }

    // Step: score
    return (
      <div>
        <div style={ms.section}>
          <div style={ms.sectionTitle}>Round Details</div>
          {resvReadOnlyCourse && (
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
              From reservation: <strong style={{ color: '#374151' }}>{resvReadOnlyCourse}</strong>
            </div>
          )}
          <div style={ms.row2}>
            {renderCourseSelect()}
            {renderDatePicker()}
          </div>
          <div style={ms.row2}>
            {renderTeeboxSelect()}
            {renderHolesToggle()}
          </div>
        </div>
        {renderPlayerEntry(false)}
        {renderBonusSection()}
      </div>
    );
  };

  const renderTournament = () => {
    // All scheduled rounds with a date (incl. leaderboard/final rounds, which also
    // get groups and need scores entered). Used for the 'no schedule' check + sessions.
    const playRounds = schedRounds.filter(r => r.slot_date);
    // Day dropdown includes every day of the tournament schedule.
    const days = Array.from(new Set(
      playRounds.map(r => r.slot_date as string)
    )).sort();
    // Sessions for the chosen day, with how many of their expected rounds (one
    // per reservation) have been entered.
    type SessionOption = { am_pm: string; has_round: boolean; expected: number; entered: number };
    const sessionsForDay = selectedDay
      ? playRounds
          .filter(r => r.slot_date === selectedDay)
          .map(r => ({
            am_pm: r.am_pm as string,
            has_round: !!r.has_round,
            expected: r.entry_summary?.rounds_expected ?? 0,
            entered: r.entry_summary?.rounds_entered ?? 0,
          }))
          // de-dupe by am_pm, keeping has_round if any matching round is entered
          .reduce((acc, cur) => {
            const ex = acc.find(a => a.am_pm === cur.am_pm);
            if (ex) {
              ex.has_round = ex.has_round || cur.has_round;
              ex.expected += cur.expected; ex.entered += cur.entered;
            } else acc.push({ ...cur });
            return acc;
          }, [] as SessionOption[])
      : [];
    const sessionLabel = (s: string) => (s === 'AM' ? 'Morning' : s === 'PM' ? 'Afternoon' : s);
    const sessionOptionLabel = (s: SessionOption) => {
      const name = sessionLabel(s.am_pm);
      if (s.expected === 0) return s.has_round ? `${name} — entered` : name;
      return `${name} — ${s.entered} of ${s.expected} round${s.expected === 1 ? '' : 's'} entered`;
    };
    const selectedSessionEntered = sessionsForDay.find(s => s.am_pm === selectedSession)?.has_round;
    const totalPlayers = playerEntries.filter(p => p.player_id != null).length;
    // Round details show once there's something to enter: a ticked reservation,
    // or — for a session with no reservations or groups at all — straight away.
    const showMatch = selectedRoundNum != null && !selectedSessionEntered
      && (resvList.length === 0 || selectedResvs.length > 0);

    return (
      <div>
        <div style={ms.section}>
          <div style={ms.sectionTitle}>Tournament & Day</div>
          <div style={ms.formGroup}>
            <label style={ms.label}>Tournament / Major</label>
            <select
              style={{ ...ms.select, ...(errors.tournament ? ms.inputError : {}) }}
              value={tournamentForRound}
              onChange={e => { setTournamentForRound(e.target.value); setErrors(prev => { const e2 = { ...prev }; delete e2.tournament; return e2; }); }}
            >
              <option value="" disabled>Select an open tournament</option>
              {openTournamentsForRound.map(t => (
                <option key={t.tournament_id} value={t.tournament_id}>{t.tournament_name}</option>
              ))}
            </select>
            {errors.tournament && <div style={ms.errMsg}>{errors.tournament}</div>}
          </div>

          {tournamentForRound && days.length === 0 && (
            <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 12 }}>
              No schedule configured for this tournament yet.
            </div>
          )}

          {days.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', columnGap: 12 }}>
              <div style={ms.formGroup}>
                <label style={ms.label}>Day</label>
                <select
                  style={{ ...ms.select, ...(errors.round ? ms.inputError : {}) }}
                  value={selectedDay}
                  onChange={e => { setSelectedDay(e.target.value); setSelectedSession(''); setErrors(prev => { const e2 = { ...prev }; delete e2.round; return e2; }); }}
                >
                  <option value="" disabled>Select a day</option>
                  {days.map((d, i) => (
                    <option key={d} value={d}>Day {i + 1} — {d}</option>
                  ))}
                </select>
              </div>
              <div style={ms.formGroup}>
                <label style={ms.label}>Session</label>
                <select
                  style={{ ...ms.select, ...(errors.round ? ms.inputError : {}) }}
                  value={selectedSession}
                  onChange={e => { setSelectedSession(e.target.value); setErrors(prev => { const e2 = { ...prev }; delete e2.round; return e2; }); }}
                  disabled={!selectedDay}
                >
                  <option value="" disabled>Select session</option>
                  {sessionsForDay.map(sess => (
                    <option key={sess.am_pm} value={sess.am_pm} disabled={sess.has_round}>
                      {sessionOptionLabel(sess)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
          {errors.round && <div style={ms.errMsg}>{errors.round}</div>}

          {selectedSessionEntered && (
            <div style={{ fontSize: 13, color: '#dc2626', marginTop: 8 }}>
              Every round of this session has been entered — pick another session.
            </div>
          )}

          {selectedDay && selectedSession && !selectedSessionEntered && selectedRoundNum == null && (
            <div style={{ fontSize: 13, color: '#dc2626', marginTop: 8 }}>
              No round is scheduled for that day and session.
            </div>
          )}
        </div>

        {selectedRoundNum != null && !selectedSessionEntered && resvList.length > 0 && (
          <div style={ms.section}>
            <div style={ms.sectionTitle}>Reservations</div>
            <p style={{ fontSize: 12.5, color: '#64748b', margin: '0 0 10px' }}>
              Pick the reservation(s) this round was played on — several only if they're on the same course.
            </p>
            {errors.parts && <div style={{ ...ms.errMsg, marginBottom: 8 }}>{errors.parts}</div>}
            <div style={ms.resvList}>
              {resvList.map(r => {
                const selected = selectedKeys.includes(r.key);
                const otherCourse = isOpen(r) && !selected && !canAdd(r, selectedResvs);
                const disabled = !isOpen(r) || otherCourse;
                return (
                  <label key={r.key} style={ms.resvItem(selected, disabled)}>
                    <input
                      type="checkbox"
                      checked={selected}
                      disabled={disabled}
                      onChange={() => toggleReservation(r)}
                      style={ms.resvCheckbox}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '2px 8px', fontSize: 13 }}>
                        <span style={{ fontWeight: 700, color: '#0f172a' }}>{r.course_name || 'Course not set'}</span>
                        <span style={{ color: '#64748b' }}>
                          {r.tee_time || (r.reservation_id == null ? `Group ${r.group_number} · no tee time` : 'Tee time')}
                        </span>
                        {r.entered && (
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#15803d', background: '#dcfce7', borderRadius: 5, padding: '1px 7px' }}>
                            ✓ Entered
                          </span>
                        )}
                        {otherCourse && <span style={{ fontSize: 11, color: '#b45309' }}>Other course</span>}
                      </div>
                      <div style={ms.resvMeta}>{r.players.map(pl => `${pl.first_name} ${pl.last_name}`).join(', ')}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {showMatch && (
          <>
            <div style={ms.section}>
              <div style={ms.sectionTitle}>Derived From Schedule</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 13, color: '#374151', marginBottom: 4 }}>
                <div><span style={{ color: '#94a3b8' }}>Course: </span><strong>{tournReadOnlyCourse || '—'}</strong></div>
                <div><span style={{ color: '#94a3b8' }}>Date: </span><strong>{tournReadOnlyDate || '—'}</strong></div>
                {selectedResvs.length > 0 && (
                  <div>
                    <span style={{ color: '#94a3b8' }}>Tee times: </span>
                    <strong>{selectedResvs.map(r => r.tee_time || `Group ${r.group_number}`).join(', ')}</strong>
                  </div>
                )}
                <div><span style={{ color: '#94a3b8' }}>Players: </span><strong>{totalPlayers}</strong></div>
              </div>
            </div>

            <div style={ms.section}>
              <div style={ms.sectionTitle}>Round Setup</div>
              <div style={ms.row2}>
                {tournReadOnlyDate ? (
                  <div style={ms.formGroup}>
                    <label style={ms.label}>Date</label>
                    <div style={ms.readOnlyField}>{tournReadOnlyDate}</div>
                  </div>
                ) : renderDatePicker()}
                {tournReadOnlyCourse ? (
                  <div style={ms.formGroup}>
                    <label style={ms.label}>Course</label>
                    <div style={ms.readOnlyField}>{tournReadOnlyCourse}</div>
                  </div>
                ) : renderCourseSelect()}
              </div>
              <div style={ms.row2}>
                {renderTeeboxSelect()}
                {renderHolesToggle()}
              </div>
            </div>
            {renderPlayerEntry(true)}
          </>
        )}
      </div>
    );
  };

  // ── Back button handler ──────────────────────────────────────────────────
  const handleBack = () => {
    if (path === 'reservation' && reservStep === 'score') {
      setReservStep('pick');
    } else {
      setPath('choose');
      setReservStep('pick');
      setCourseId('');
      setTeeboxId('');
      setTeeboxes([]);
      setPlayerEntries([emptyPlayer(), emptyPlayer()]);
      // Start the tournament path afresh next time (its players derive from
      // the tee-time selection, which would otherwise be left behind).
      setTournamentForRound('');
      setErrors({});
    }
  };

  const headerTitle = path === 'choose' ? 'Add New Round'
    : path === 'manual' ? 'Manual Entry'
    : path === 'reservation' ? (reservStep === 'pick' ? 'Select Reservations' : 'Enter Scores')
    : 'Tournament Match';

  if (!show) return null;

  return (
    <div style={ms.overlay} onClick={e => e.target === e.currentTarget && onHide()}>
      <div style={ms.box}>
        {/* Header */}
        <div style={ms.header}>
          {path !== 'choose' && (
            <button type="button" style={ms.backBtn} onClick={handleBack}>← Back</button>
          )}
          <h2 style={ms.headerTitle}>{headerTitle}</h2>
          <button type="button" style={ms.closeBtn} onClick={onHide}>×</button>
        </div>

        {/* Body */}
        <div style={ms.body}>
          {path === 'choose' && renderChoose()}
          {path === 'manual' && renderManual()}
          {path === 'reservation' && renderReservation()}
          {path === 'tournament' && renderTournament()}
        </div>

        {/* Footer */}
        {path !== 'choose' && (
          <div style={ms.footer}>
            <button type="button" style={ms.cancelBtn} onClick={onHide}>Cancel</button>
            {path === 'reservation' && reservStep === 'pick' ? (
              <button
                type="button"
                style={ms.primaryBtn(false)}
                onClick={proceedToScore}
                disabled={selectedResvIds.size === 0}
              >
                Continue →
              </button>
            ) : path === 'tournament' && (selectedRoundNum == null || (resvList.length > 0 && selectedResvs.length === 0)) ? (
              <button type="button" style={ms.primaryBtn(true)} disabled>Save Round</button>
            ) : (
              <button
                type="button"
                style={ms.primaryBtn(saving)}
                disabled={saving}
                onClick={() => save(path === 'tournament')}
              >
                {saving ? 'Saving…' : 'Save Round'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;
